import { Boxes, ExternalLink, FolderKanban, Globe, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Section } from '@/components/common/Blocks'
import { RowMenu } from '@/components/common/RowMenu'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useCustomerModules } from '../../api'
import type { Customer } from '../../types'
import { useCrm } from '../../ui'
import { DeliveryBadge, ModuleStatusBadge } from '../CrmBits'
import type { CrmDialog } from '../CrmDialogs'
import { LiveLinks } from '../LiveLinks'
import { useCrmDeletes } from '../useCrmDeletes'

/** Müşterinin kullandığı proje ve satın aldığı/denediği modüller. */
export function ProjectTab({ customer, canWrite, open }: { customer: Customer; canWrite: boolean; open: (d: CrmDialog) => void }) {
  const editCustomer = () => open({ kind: 'customer' })
  const { t } = useTranslation()
  const modules = useCustomerModules()
  const deletes = useCrmDeletes()
  const { projectName } = useCrm()
  const rows = (modules.data ?? []).filter((m) => m.customerId === customer.id)

  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-4 shadow-card">
        <span className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <FolderKanban className="size-6" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{t('crm.fields.project')}</p>
          <p className="text-lg font-semibold">{projectName(customer.projectId)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{t('crm.project.moduleCount', { count: rows.length })}</span>
            <DeliveryBadge delivery={customer.delivery} />
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/projects/${customer.projectId}`}>
            {t('crm.project.open')}
            <ExternalLink />
          </Link>
        </Button>
      </section>

      <div className="space-y-2">
        <h2 className="flex items-center gap-2 font-semibold">
          <Globe className="size-4 text-muted-foreground" aria-hidden />
          {t('crm.links.title')}
          <span className="text-xs font-normal text-muted-foreground">{t('crm.links.subtitle')}</span>
        </h2>
        <LiveLinks customer={customer} canWrite={canWrite} onEdit={editCustomer} variant="tiles" />
      </div>

      <Section
        title={t('crm.project.modules')}
        icon={Boxes}
        bodyClassName="p-0"
        actions={
          canWrite ? (
            <Button size="sm" onClick={() => open({ kind: 'module', module: null })}>
              <Plus />
              {t('crm.module.add')}
            </Button>
          ) : null
        }
      >
        {rows.length === 0 ? (
          <EmptyState icon={Boxes} title={t('crm.module.empty')} />
        ) : (
          <ul className="divide-y">
            {rows.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(`crm.license.${m.licenseType}`)} · {t('crm.module.since', { date: fmtDate(m.startDate) })}
                  </p>
                </div>
                <span className="font-semibold tabular-nums">{fmtMoney(m.price)}</span>
                <ModuleStatusBadge status={m.status} />
                {canWrite ? <RowMenu label={m.name} onEdit={() => open({ kind: 'module', module: m })} onDelete={() => void deletes.module(m)} /> : null}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}
